import {
    Fragment,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode
} from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import { useIsScrolling } from './useIsScrolling';

/** Chip gap (px) and the width reserved for the "+N" overflow chip. */
const GAP = 4;
const OVERFLOW_WIDTH = 48;

interface OverflowChipsProps<T> {
    items: readonly T[];
    /** Stable key per item (the chip's React key, and the measurer's). */
    getKey: (item: T) => string;
    /** How one item renders. Called for the measurer AND the visible row. */
    renderChip: (item: T) => ReactNode;
}

/**
 * A row of chips that shows only as many as FIT, then a "+N" chip opening a
 * popover with all of them. Widths are measured off-layout (every chip at its
 * natural width, icons included) so the visible row can never cut one.
 *
 * Shared by the Befund reference cells and the Befundliste's per-type reference
 * columns; the chips themselves are the caller's.
 *
 * Everything below is arranged so that a cell which is merely RE-RENDERED does
 * no measuring, mounts no second copy of its chips and instantiates no popover.
 * A CPU profile of one Befundliste scroll found this the most expensive thing in
 * the whole app — 2.3 s of a 12.7 s trace, and the top entry in Chrome's
 * forced-reflow report. The notes below say what each guard is holding back;
 * read them before making any of it unconditional again.
 */
export default function OverflowChips<T>({
    items,
    getKey,
    renderChip
}: OverflowChipsProps<T>) {
    // Held as STATE, not a ref: the popover anchors to this element, and an
    // anchor read out of a ref during render is read before the ref is filled.
    const [outer, setOuter] = useState<HTMLDivElement | null>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    // `null` until the widths have actually been read — which is not the same
    // as "all of them fit", and the difference matters while scrolling (see
    // `shownCount`).
    const [visibleCount, setVisibleCount] = useState<number | null>(null);
    const [expanded, setExpanded] = useState(false);
    // Latched separately from `expanded`: the popover's copy of the chips is
    // built the first time it opens and kept afterwards, so the closing fade
    // does not play over an emptied paper.
    const [everExpanded, setEverExpanded] = useState(false);

    // A lone chip is shown whether it fits or not (see `Math.max(count, 1)`
    // below), so there is nothing to decide — and most reference cells carry
    // exactly one. Measuring anyway mounted a second, hidden copy of every chip
    // in the grid to answer a question with a single possible answer.
    const measured = items.length > 1;

    // A virtualised grid builds and tears down its rows continuously while it
    // moves, and that — not the re-rendering — is what scrolling one of these
    // lists costs. So while it moves, this cell leaves out the hidden measurer,
    // the measuring itself and the popover. The chips stay.
    const scrolling = useIsScrolling();

    /**
     * What the measurement depends on as a VALUE rather than as an identity.
     * Every call site builds `items` in its render body, so keying the effect on
     * the array itself re-ran it on every render: rebuilding the ResizeObserver
     * and — the expensive half — forcing a synchronous layout per cell per frame
     * while scrolling.
     *
     * Keying on the KEYS is deliberately not the whole story: a chip's label can
     * change while its key does not (a reference resolver arriving after the
     * rows), and a label is what a width is made of. That case is caught by
     * observing the measurer itself below instead.
     */
    const itemsKey = items.map(getKey).join('|');

    // How many chips fit the cell width (reserving room for the "+N" chip when
    // some are hidden).
    useLayoutEffect(() => {
        const measure = measureRef.current;
        if (!outer || !measure || !measured || scrolling) {
            return;
        }
        const recompute = () => {
            const available = outer.clientWidth;
            const widths = Array.from(measure.children).map(
                (child) => (child as HTMLElement).offsetWidth
            );
            let used = 0;
            let count = 0;
            for (let index = 0; index < widths.length; index++) {
                const next = used + (index > 0 ? GAP : 0) + widths[index];
                const moreAfter = index < widths.length - 1;
                if (
                    next + (moreAfter ? GAP + OVERFLOW_WIDTH : 0) <=
                    available
                ) {
                    used = next;
                    count = index + 1;
                } else {
                    break;
                }
            }
            // Always show at least one chip so a narrow cell isn't only a badge.
            setVisibleCount(widths.length ? Math.max(count, 1) : 0);
        };
        recompute();
        /**
         * BOTH boxes. `outer` is the cell getting wider or narrower; `measure`
         * is the chips themselves changing width — which happens without the
         * keys changing at all, the moment a label resolves from a raw id into
         * "Bef. 7000, Fl. 1, Pl. 1". Without it such a cell keeps the count it
         * measured against the ids for the rest of its life.
         */
        const observer = new ResizeObserver(recompute);
        observer.observe(outer);
        observer.observe(measure);
        return () => observer.disconnect();
    }, [itemsKey, measured, outer, scrolling]);

    /**
     * How many chips to show, in the three states this can be in.
     *
     * Measured: what was measured, CLAMPED — a shrinking list can outrun the
     * effect by a render, and a negative overflow count would print "+-1".
     *
     * Not measured yet, standing still: all of them. The measuring effect runs
     * before the browser paints, so a cell whose chips all fit settles without a
     * second render.
     *
     * Not measured yet, and moving: ONE, plus the "+N" badge — better than a
     * full row cut off mid-word. It becomes the real thing once the list stops.
     */
    const shownCount = (() => {
        if (!measured) {
            return items.length;
        }
        if (visibleCount !== null) {
            return Math.min(visibleCount, items.length);
        }
        return scrolling ? 1 : items.length;
    })();
    const hiddenCount = items.length - shownCount;

    return (
        <Box
            ref={setOuter}
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: `${GAP}px`,
                width: '100%',
                /**
                 * Fill the cell so the chips sit in its MIDDLE — a cell spanning
                 * several rows (the Befundliste's Befund columns) is taller than
                 * one line, and a chip pinned to its top reads as belonging to
                 * the first row.
                 */
                height: '100%',
                minWidth: 0,
                overflow: 'hidden',
                /**
                 * Let a shown chip shrink (a Chip truncates its own label).
                 * Where not even ONE fits beside the "+N" it is shown anyway,
                 * and without this it would push the "+N" out of reach. The
                 * measurer is excluded: it must stay at its NATURAL width.
                 */
                '& > *:not([aria-hidden="true"])': {
                    minWidth: 0
                }
            }}
        >
            {/* Off-layout measurer: every chip at natural width. Never while
                the list is moving — this is a second copy of every chip in the
                grid, and building it is half of what scrolling costs. */}
            {measured && !scrolling && (
                <Box
                    ref={measureRef}
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: 0,
                        visibility: 'hidden',
                        pointerEvents: 'none',
                        display: 'flex',
                        gap: `${GAP}px`,
                        whiteSpace: 'nowrap'
                    }}
                >
                    {items.map((item) => (
                        <Fragment key={getKey(item)}>
                            {renderChip(item)}
                        </Fragment>
                    ))}
                </Box>
            )}

            {items.slice(0, shownCount).map((item) => (
                <Fragment key={getKey(item)}>{renderChip(item)}</Fragment>
            ))}
            {hiddenCount > 0 && (
                <Chip
                    size="small"
                    variant="outlined"
                    label={`+${hiddenCount}`}
                    clickable
                    // The badge keeps its width — the shrinking above is meant
                    // for the labels, and a "+…" says nothing.
                    sx={{ flexShrink: 0 }}
                    onClick={(event) => {
                        event.stopPropagation();
                        setEverExpanded(true);
                        setExpanded(true);
                    }}
                />
            )}

            {/* Only where there is something to expand, and not while the list
                is moving — a Popover is a Modal, and one per cell put a
                four-figure count of them into a single scroll. The `expanded`
                half keeps an OPEN one alive through a resize or a scroll. */}
            {(expanded || (hiddenCount > 0 && !scrolling)) && (
                <Popover
                    open={expanded}
                    anchorEl={outer}
                    onClose={() => setExpanded(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    {everExpanded && (
                        <Box
                            sx={{
                                p: 1,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                maxWidth: 360
                            }}
                        >
                            {items.map((item) => (
                                <Fragment key={getKey(item)}>
                                    {renderChip(item)}
                                </Fragment>
                            ))}
                        </Box>
                    )}
                </Popover>
            )}
        </Box>
    );
}
